import { useEffect, useState } from 'react';

import { faPage } from '@fortawesome/pro-regular-svg-icons';
import cx from 'classnames';

import { IModule, TUserWithConnect } from '@cloud-wave/neon-common-lib';

import { useConfigContext } from 'lib/core/config';

import { useAIContext } from 'lib/common/contexts/AIContext';
import { useModulesContext } from 'lib/common/contexts/ModulesContext';
import { usePermissionsContext } from 'lib/common/contexts/PermissionsContext';

import UserProfile from 'lib/common/components/UserProfile';

import { SOFTPHONE_DEFAULT_NAV_OPTIONS } from 'lib/common/constants/softphoneDefaultNavOptions';

import { ConnectPermissions } from 'lib/common/types/Permissions';
import { SoftphoneNavOption } from 'lib/common/types/SoftphoneNavOption';
import { getModuleIcon } from 'lib/common/utils/getModuleIcon';

import SoftphoneAddAgentTaskButton from './components/SoftphoneAddAgentTaskButton';
import SoftphoneNavMenuFolder from './components/SoftphoneNavMenuFolder';
import SoftphoneNavMenuItem from './components/SoftphoneNavMenuItem';
import SoftphoneOpenIQModalButton from './components/SoftphoneOpenIQModalButton';
import SoftphoneTaskList from './components/SoftphoneTaskList';

import './softphone-sidebar.scss';

interface ISoftphoneSidebar {
  user: TUserWithConnect;
  isReversedLayout: boolean;
}

export default function SoftphoneSidebar({ user, isReversedLayout }: ISoftphoneSidebar) {
  const {
    state: { pageModules, modulesLoaded },
  } = useModulesContext();
  const { config } = useConfigContext();
  const { hasPermission } = usePermissionsContext();
  const [navOptions, setNavOptions] = useState<any>(SOFTPHONE_DEFAULT_NAV_OPTIONS(config));
  const [navLoaded, setNavLoaded] = useState(false);

  const {
    state: { IQEnabled },
  } = useAIContext();

  useEffect(() => {
    if (navLoaded || !modulesLoaded) {
      return;
    }

    setNavOptions([
      ...navOptions,
      ...pageModules.map((pageModule: IModule) => ({
        link: `/${pageModule.pathName}`,
        name: pageModule.name,
        icon: getModuleIcon({ icon: pageModule.icon, defaultIcon: faPage }),
      })),
    ]);

    setNavLoaded(true);
  }, [modulesLoaded]);

  const isCreateTaskDisabled = hasPermission({ type: 'connect', permission: ConnectPermissions.CREATE_TASK_DISABLED });

  return (
    <nav className={cx('softphone-sidebar', { 'softphone-sidebar--reversed': isReversedLayout })}>
      <div className="softphone-sidebar__top">
        <SoftphoneAddAgentTaskButton />
        <SoftphoneTaskList isCreateTaskDisabled={isCreateTaskDisabled} isReversedLayout={isReversedLayout} />
      </div>
      <div className="softphone-sidebar__nav-menu" data-testid="softphone-sidebar-nav-menu">
        {IQEnabled && <SoftphoneOpenIQModalButton />}
        {navOptions.map((item: SoftphoneNavOption) =>
          item.childOptions ? (
            <SoftphoneNavMenuFolder key={item.name} {...item} isReversedLayout={isReversedLayout} />
          ) : (
            <SoftphoneNavMenuItem key={item.link} {...item} />
          )
        )}
      </div>
      <UserProfile user={user} isReversedPopover={isReversedLayout} />
    </nav>
  );
}
